<template>
  <section class="admin-content" id="vendor-products-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-package" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">My Products</h4>
                <small style="opacity: .8;">Your marketplace catalogue: prices, stock and photos</small>
              </div>
            </div>
            <button type="button" class="btn btn-light btn-sm" @click="newProduct">
              <i class="fe fe-plus mr-1"></i>Add product
            </button>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="vendor && vendor.status !== 'approved'" class="alert" :class="vendor.status === 'pending' ? 'alert-warning' : 'alert-danger'">
            <strong>Your business is {{ vendor.status }}.</strong>
            <span v-if="vendor.status === 'pending'"> Products you add are saved but not listed until the marketplace team approves your account.</span>
            <span v-else> Your products are not listed. <span v-if="vendor.status_reason">Reason: {{ vendor.status_reason }}</span></span>
          </div>

          <div class="row">
            <!-- List -->
            <div :class="editing ? 'col-lg-7' : 'col-12'" class="mb-4">
              <div class="card shadow-sm table-card">
                <div class="d-flex align-items-center flex-wrap px-4 pt-4 pb-3" style="gap: 8px;">
                  <label for="vp-q" class="sr-only">Search products</label>
                  <input id="vp-q" v-model="q" type="search" class="form-control form-control-sm styled-input" style="max-width: 260px;"
                    placeholder="Search name, SKU, brand…" @keyup.enter="fetchProducts" />
                  <label for="vp-cat" class="sr-only">Category</label>
                  <select id="vp-cat" v-model="category" class="form-control form-control-sm styled-input" style="max-width: 220px;" @change="fetchProducts">
                    <option value="">All categories</option>
                    <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
                  </select>
                  <div class="custom-control custom-checkbox ml-2">
                    <input id="vp-low" v-model="lowStock" type="checkbox" class="custom-control-input" @change="fetchProducts" />
                    <label class="custom-control-label small" for="vp-low">Low stock</label>
                  </div>
                  <span class="badge badge-soft-info ml-auto">{{ products.length }}</span>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Product</th>
                        <th class="table-th">Price</th>
                        <th class="table-th">Stock</th>
                        <th class="table-th">Status</th>
                        <th class="table-th"><span class="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in products" :key="p.id" class="table-row" :class="{ 'row-active': editing && form.id === p.id }">
                        <td class="px-4 py-3">
                          <div class="d-flex align-items-center" style="gap: 10px;">
                            <div class="thumb">
                              <img v-if="p.image_urls.length" :src="p.image_urls[0]" :alt="p.name" />
                              <i v-else class="fe fe-image"></i>
                            </div>
                            <div>
                              <div class="font-weight-bold" style="font-size:.88rem;">{{ p.name }}</div>
                              <div class="small text-muted">{{ p.category }}<span v-if="p.sku"> · {{ p.sku }}</span></div>
                            </div>
                          </div>
                        </td>
                        <td class="py-3">{{ money(p.unit_price) }}<div class="small text-muted">/ {{ p.unit }}</div></td>
                        <td class="py-3">
                          <span :class="{ 'text-danger font-weight-bold': p.reorder_level !== null && p.quantity_available <= p.reorder_level }">
                            {{ p.quantity_available }}
                          </span>
                        </td>
                        <td class="py-3">
                          <span class="status-badge" :class="p.is_active ? 'status-badge--on' : 'status-badge--off'">{{ p.is_active ? "Active" : "Hidden" }}</span>
                        </td>
                        <td class="py-3 pr-4 text-right text-nowrap">
                          <button type="button" class="btn btn-sm btn-view" @click="edit(p)"><i class="fe fe-edit-2 mr-1"></i>Edit</button>
                        </td>
                      </tr>
                      <tr v-if="!products.length">
                        <td colspan="5" class="text-center py-5 text-muted">
                          <i class="fe fe-package" style="font-size:2rem;opacity:.3"></i>
                          <p class="mt-2 mb-0">No products yet. Add your first product.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Editor -->
            <div class="col-lg-5 mb-4" v-if="editing">
              <div class="card shadow-sm form-card">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-edit-2" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">{{ form.id ? "Edit product" : "New product" }}</h6>
                  <button type="button" class="btn btn-sm btn-link text-white ml-auto" aria-label="Close editor" @click="editing = false">
                    <i class="fe fe-x"></i>
                  </button>
                </div>
                <form class="card-body px-4 py-4" @submit.prevent="save">
                  <div class="form-group">
                    <label for="pf-name" class="form-label-sm">Name *</label>
                    <input id="pf-name" v-model="form.name" class="form-control styled-input" required />
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-7">
                      <label for="pf-cat" class="form-label-sm">Category *</label>
                      <select id="pf-cat" v-model="form.category" class="form-control styled-input" required>
                        <option value="">Select…</option>
                        <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
                      </select>
                    </div>
                    <div class="form-group col-md-5">
                      <label for="pf-brand" class="form-label-sm">Brand</label>
                      <input id="pf-brand" v-model="form.brand" class="form-control styled-input" />
                    </div>
                    <div class="form-group col-md-6">
                      <label for="pf-price" class="form-label-sm">Unit price (₦) *</label>
                      <input id="pf-price" v-model.number="form.unit_price" type="number" min="0" step="0.01" class="form-control styled-input" required />
                    </div>
                    <div class="form-group col-md-6">
                      <label for="pf-unit" class="form-label-sm">Unit</label>
                      <input id="pf-unit" v-model="form.unit" class="form-control styled-input" placeholder="piece, box, pack…" />
                    </div>
                    <div class="form-group col-md-4">
                      <label for="pf-qty" class="form-label-sm">In stock</label>
                      <input id="pf-qty" v-model.number="form.quantity_available" type="number" min="0" class="form-control styled-input" />
                    </div>
                    <div class="form-group col-md-4">
                      <label for="pf-min" class="form-label-sm">Min. order</label>
                      <input id="pf-min" v-model.number="form.min_order_quantity" type="number" min="1" class="form-control styled-input" />
                    </div>
                    <div class="form-group col-md-4">
                      <label for="pf-reorder" class="form-label-sm">Reorder at</label>
                      <input id="pf-reorder" v-model.number="form.reorder_level" type="number" min="0" class="form-control styled-input" />
                    </div>
                    <div class="form-group col-md-6">
                      <label for="pf-sku" class="form-label-sm">SKU</label>
                      <input id="pf-sku" v-model="form.sku" class="form-control styled-input" />
                    </div>
                    <div class="form-group col-md-6 d-flex align-items-end">
                      <div class="custom-control custom-switch mb-2">
                        <input id="pf-active" v-model="form.is_active" type="checkbox" class="custom-control-input" />
                        <label class="custom-control-label small" for="pf-active">Listed (visible to buyers)</label>
                      </div>
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="pf-desc" class="form-label-sm">Description</label>
                    <textarea id="pf-desc" v-model="form.description" rows="4" class="form-control styled-input"></textarea>
                  </div>

                  <div class="form-group" v-if="form.id">
                    <span class="form-label-sm">Photos</span>
                    <div class="d-flex flex-wrap" style="gap: 8px;">
                      <div class="photo" v-for="(url, i) in form.image_urls" :key="url">
                        <img :src="url" :alt="form.name + ' photo ' + (i + 1)" />
                        <button type="button" class="photo-del" :aria-label="'Remove photo ' + (i + 1)" @click="removeImage(i)">
                          <i class="fe fe-x"></i>
                        </button>
                      </div>
                      <label for="pf-images" class="photo photo-add" v-if="form.image_urls.length < 6">
                        <i class="fe fe-plus"></i>
                        <span class="sr-only">Add photos</span>
                      </label>
                      <input id="pf-images" ref="images" type="file" accept="image/jpeg,image/png,image/webp" multiple class="d-none" @change="uploadImages" />
                    </div>
                    <small class="text-muted">JPG, PNG or WebP, up to 3 MB each, 6 photos.</small>
                  </div>
                  <p class="small text-muted" v-else>Save the product first, then add photos.</p>

                  <StandardItemTags v-if="form.id" :product-id="form.id" />

                  <div v-if="errors.length" class="alert alert-danger small py-2">
                    <div v-for="e in errors" :key="e">{{ e }}</div>
                  </div>

                  <div class="d-flex" style="gap: 8px;">
                    <button type="submit" class="btn btn-submit flex-fill" :disabled="saving">
                      <i class="fe fe-save mr-1"></i>{{ form.id ? "Save changes" : "Add product" }}
                    </button>
                    <button v-if="form.id" type="button" class="btn btn-outline-danger" @click="destroy">
                      <i class="fe fe-trash-2"></i><span class="sr-only">Delete product</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import StandardItemTags from "../assessments/StandardItemTags.vue";

const blank = () => ({
  id: null, name: "", category: "", brand: "", unit_price: "", unit: "piece", quantity_available: 0,
  min_order_quantity: 1, reorder_level: "", sku: "", is_active: true, description: "", image_urls: [],
});

export default {
  components: { Navbar, StandardItemTags },
  data() {
    return {
      vendor: null,
      categories: [],
      products: [],
      q: "",
      category: "",
      lowStock: false,
      editing: false,
      saving: false,
      errors: [],
      form: blank(),
    };
  },
  created() {
    this.axios.get("/api/v1/marketplace/categories").then((res) => (this.categories = res.data.data || []));
    this.axios.get("/api/v1/auth/vendor/me").then((res) => (this.vendor = res.data.data)).catch(this.notifyError);
    this.fetchProducts();
  },
  methods: {
    money(v) {
      return "₦" + Number(v || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });
    },
    notifyError(err) {
      const data = (err.response && err.response.data) || {};
      this.$breadstick.notify(data.message || "Something went wrong", { position: "top-right" });
    },
    fetchProducts() {
      const params = { per_page: 200 };
      if (this.q) params.q = this.q;
      if (this.category) params.category = this.category;
      if (this.lowStock) params.low_stock = 1;
      this.axios.get("/api/v1/auth/vendor/products", { params }).then((res) => (this.products = res.data.data || [])).catch(this.notifyError);
    },
    newProduct() {
      this.form = blank();
      this.errors = [];
      this.editing = true;
    },
    edit(p) {
      this.form = { ...blank(), ...p, reorder_level: p.reorder_level === null ? "" : p.reorder_level };
      this.errors = [];
      this.editing = true;
    },
    payload() {
      const f = this.form;
      return {
        name: f.name, category: f.category, brand: f.brand || null, unit_price: f.unit_price, unit: f.unit || null,
        quantity_available: f.quantity_available || 0, min_order_quantity: f.min_order_quantity || 1,
        reorder_level: f.reorder_level === "" ? null : f.reorder_level, sku: f.sku || null,
        is_active: f.is_active, description: f.description || null,
      };
    },
    applySaved(product) {
      this.form = { ...blank(), ...product, reorder_level: product.reorder_level === null ? "" : product.reorder_level };
      this.fetchProducts();
    },
    save() {
      this.saving = true;
      this.errors = [];
      const request = this.form.id
        ? this.axios.put(`/api/v1/auth/vendor/products/${this.form.id}`, this.payload())
        : this.axios.post("/api/v1/auth/vendor/products", this.payload());
      request
        .then((res) => {
          this.$breadstick.notify(res.data.message || "Saved", { position: "top-right" });
          this.applySaved(res.data.data);
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {};
          this.errors = data.errors ? Object.values(data.errors).flat() : [data.message || "Could not save"];
        })
        .finally(() => (this.saving = false));
    },
    destroy() {
      if (!window.confirm(`Delete "${this.form.name}"? It will be removed from the marketplace.`)) return;
      this.axios
        .delete(`/api/v1/auth/vendor/products/${this.form.id}`)
        .then(() => {
          this.editing = false;
          this.fetchProducts();
        })
        .catch(this.notifyError);
    },
    uploadImages(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const data = new FormData();
      files.forEach((f) => data.append("images[]", f));
      this.axios
        .post(`/api/v1/auth/vendor/products/${this.form.id}/images`, data)
        .then((res) => this.applySaved(res.data.data))
        .catch((err) => {
          const d = (err.response && err.response.data) || {};
          this.errors = d.errors ? Object.values(d.errors).flat() : [d.message || "Upload failed"];
        })
        .finally(() => (this.$refs.images.value = ""));
    },
    removeImage(index) {
      this.axios
        .delete(`/api/v1/auth/vendor/products/${this.form.id}/images`, { data: { index } })
        .then((res) => this.applySaved(res.data.data))
        .catch(this.notifyError);
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.form-card { border-radius: 14px; overflow: hidden; border: none; }
.form-card-header { display: flex; align-items: center; gap: 12px; padding: 14px 24px; background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.section-icon { width: 28px; height: 28px; background: rgba(255,255,255,.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
.form-label-sm { font-weight: 600; font-size: .8rem; color: #374151; margin-bottom: 4px; display: block; }
.styled-input { border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: .88rem; }
.styled-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }
.table-card { border-radius: 12px; border: none; }
.table-th { font-size: .73rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; }
.table-row:hover, .row-active { background: #f8fafc; }
.thumb { width: 42px; height: 42px; border-radius: 8px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; color: #94a3b8; overflow: hidden; flex-shrink: 0; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: .75rem; font-weight: 600; }
.status-badge--on { background: #d1fae5; color: #065f46; }
.status-badge--off { background: #e2e8f0; color: #475569; }
.photo { position: relative; width: 72px; height: 72px; border-radius: 8px; overflow: hidden; background: #f1f5f9; display: flex; align-items: center; justify-content: center; margin: 0; }
.photo img { width: 100%; height: 100%; object-fit: cover; }
.photo-add { border: 2px dashed #cbd5e1; color: #64748b; cursor: pointer; font-size: 1.3rem; }
.photo-del { position: absolute; top: 2px; right: 2px; border: none; background: rgba(15,23,42,.7); color: #fff; border-radius: 50%; width: 20px; height: 20px; font-size: .7rem; padding: 0; }
.btn-submit { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border: none; color: #fff; border-radius: 8px; font-weight: 600; padding: 8px 20px; }
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #1e3a5f; background: #fff; font-size: .8rem; }
</style>
